import React, { useState, useCallback } from 'react'
import { Download, Check, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'

interface ExcelDownloadButtonProps {
    /** Function to trigger export — should call the appropriate exportToExcel function */
    onExport: () => void
    /** Optional label text (default: hidden, icon-only) */
    label?: string
    /** Optional className for custom styling */
    className?: string
    /** Disabled state */
    disabled?: boolean
    /** Tooltip text */
    title?: string
}

/**
 * Reusable Excel download button with visual feedback.
 * Shows a download icon → spinner → checkmark animation on click.
 */
export const ExcelDownloadButton: React.FC<ExcelDownloadButtonProps> = ({
    onExport,
    label,
    className,
    disabled = false,
    title = 'Download as Excel',
}) => {
    const [state, setState] = useState<'idle' | 'exporting' | 'done'>('idle')

    const handleClick = useCallback(() => {
        if (state !== 'idle' || disabled) return

        setState('exporting')

        // Use requestAnimationFrame to avoid blocking the UI
        requestAnimationFrame(() => {
            try {
                onExport()
                setState('done')
            } catch (err) {
                console.error('[ExcelExport] Export failed:', err)
                setState('idle')
                return
            }

            // Reset after showing the checkmark
            setTimeout(() => setState('idle'), 1800)
        })
    }, [onExport, state, disabled])

    return (
        <button
            onClick={handleClick}
            disabled={disabled || state !== 'idle'}
            title={title}
            className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-all duration-200 shadow-sm cursor-pointer select-none',
                state === 'done'
                    ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400'
                    : state === 'exporting'
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300 dark:hover:border-emerald-800 hover:text-emerald-600 dark:hover:text-emerald-400',
                disabled && 'opacity-40 cursor-not-allowed',
                className,
            )}
        >
            {state === 'done' ? (
                <Check className="h-3.5 w-3.5 animate-[scale-in_0.2s_ease-out]" strokeWidth={3} />
            ) : state === 'exporting' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
                <Download className="h-3.5 w-3.5" />
            )}
            {label && <span>{state === 'done' ? 'Downloaded!' : label}</span>}
        </button>
    )
}
